class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    this.deferredSubmit = this.hasAttribute('data-rw-deferred-submit');

    if (this.deferredSubmit) {
      this.onDesktopDropdownToggle = this.onDesktopDropdownToggle.bind(this);
      this.onDesktopOutsideClick = this.onDesktopOutsideClick.bind(this);
      this.onDesktopDeferredChange = this.onDesktopDeferredChange.bind(this);
      this.onDesktopPendingFacetRemove = this.onDesktopPendingFacetRemove.bind(this);
      const applyButton = this.querySelector('[data-rw-facets-apply]');
      if (applyButton) applyButton.addEventListener('click', this.onDeferredSubmit.bind(this));
      facetForm.addEventListener('submit', this.onDeferredSubmit.bind(this));
      facetForm.addEventListener('change', this.onDesktopDeferredChange);
      facetForm.addEventListener('click', this.onDesktopPendingFacetRemove);
      this.querySelectorAll('.rw-desktop-facets__dropdown').forEach((dropdown) => {
        dropdown.addEventListener('toggle', this.onDesktopDropdownToggle);
        this.updateDesktopDropdownSummary(dropdown);
      });
      document.addEventListener('click', this.onDesktopOutsideClick);
    } else {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    FacetFiltersForm.movePreorderBeforeAvailability();
  }

  disconnectedCallback() {
    if (!this.deferredSubmit) return;
    document.removeEventListener('click', this.onDesktopOutsideClick);
  }

  onDesktopDropdownToggle(event) {
    const activeDropdown = event.currentTarget;
    if (!activeDropdown.open) return;

    this.querySelectorAll('.rw-desktop-facets__dropdown[open]').forEach((dropdown) => {
      if (dropdown !== activeDropdown) dropdown.removeAttribute('open');
    });
  }

  onDesktopOutsideClick(event) {
    if (this.contains(event.target)) return;
    this.querySelectorAll('.rw-desktop-facets__dropdown[open]').forEach((dropdown) => {
      dropdown.removeAttribute('open');
    });
  }

  onDesktopDeferredChange(event) {
    const dropdown = event.target.closest('.rw-desktop-facets__dropdown');
    if (dropdown && event.target.type === 'checkbox') {
      this.updateDesktopDropdownSummary(dropdown);
    }

    if (event.target.matches('[data-rw-filter-chip-label], [data-rw-price-role]')) {
      this.renderDesktopPendingFacets();
    }
  }

  renderDesktopPendingFacets() {
    const chipContainer = this.querySelector('[data-rw-collection-active-filters]');
    if (!chipContainer) return;

    const pendingFilters = Array.from(this.querySelectorAll('[data-rw-filter-chip-label]:checked')).map((input) => ({
      id: input.id,
      label: input.dataset.rwFilterChipLabel
    }));
    const minInput = this.querySelector('[data-rw-price-role="min"]');
    const maxInput = this.querySelector('[data-rw-price-role="max"]');
    const minValue = PriceRange.extractNumber(minInput?.value);
    const maxValue = PriceRange.extractNumber(maxInput?.value);
    const rangeMax = PriceRange.extractNumber(maxInput?.getAttribute('max')) || 0;
    const hasPriceFilter = (minValue !== null && minValue > 0) || (maxValue !== null && maxValue < rangeMax);

    if (hasPriceFilter) {
      const currency = chipContainer.dataset.rwFilterCurrency || 'IQD';
      pendingFilters.push({
        id: 'rw-pending-price',
        price: true,
        label: `${(minValue || 0).toLocaleString('en-US')}–${(maxValue ?? rangeMax).toLocaleString('en-US')} ${currency}`
      });
    }

    chipContainer.replaceChildren(...pendingFilters.map((filter) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'active-facets__button active-facets__button--light rw-pending-facet-chip';
      button.dataset.rwRemovePendingFilter = filter.id;
      if (filter.price) button.dataset.rwPendingPrice = 'true';
      button.setAttribute('aria-label', `Remove ${filter.label}`);

      const content = document.createElement('span');
      content.className = 'active-facets__button-inner button button--tertiary';
      content.append(document.createTextNode(filter.label));

      const close = document.createElement('span');
      close.className = 'rw-pending-facet-chip__close';
      close.setAttribute('aria-hidden', 'true');
      close.textContent = '×';
      content.append(close);
      button.append(content);
      return button;
    }));

    chipContainer.toggleAttribute('hidden', pendingFilters.length === 0);
    const count = this.querySelector('.rw-desktop-facets__count');
    if (count) {
      count.textContent = `${pendingFilters.length} ${count.dataset.rwSelectedLabel || ''}`.trim();
      count.toggleAttribute('hidden', pendingFilters.length === 0);
    }
  }

  onDesktopPendingFacetRemove(event) {
    const button = event.target.closest('[data-rw-remove-pending-filter]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();

    if (button.dataset.rwPendingPrice === 'true') {
      this.querySelectorAll('[data-rw-price-role]').forEach((input) => {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    } else {
      const input = this.querySelector(`#${CSS.escape(button.dataset.rwRemovePendingFilter)}`);
      if (input) {
        input.checked = false;
        const dropdown = input.closest('.rw-desktop-facets__dropdown');
        if (dropdown) this.updateDesktopDropdownSummary(dropdown);
      }
    }

    this.renderDesktopPendingFacets();
    this.onDeferredSubmit(event);
  }

  updateDesktopDropdownSummary(dropdown) {
    const summary = dropdown.querySelector('[data-rw-facets-selection-summary]');
    if (!summary) return;

    const selectedLabels = Array.from(dropdown.querySelectorAll('input[type="checkbox"]:checked'))
      .map((input) => input.closest('label')?.querySelector('span')?.textContent.trim())
      .filter(Boolean);

    if (!selectedLabels.length) {
      summary.textContent = summary.dataset.rwEmptyLabel || '';
      return;
    }

    summary.textContent = selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels[0]} (${selectedLabels.length})`;
  }

  static isPreorderLabel(labelText) {
    const normalized = (labelText || '').toLowerCase().trim();
    return (
      normalized.includes('pre-order') ||
      normalized.includes('pre order') ||
      normalized.includes('preorder') ||
      normalized.includes('طلب مسبق')
    );
  }

  static isAvailabilityLabel(labelText) {
    const normalized = (labelText || '').toLowerCase().trim();
    return normalized.includes('availability') || normalized.includes('التوفر');
  }

  static reorderFacetGroup(container) {
    if (!container) return;

    const filterNodes = Array.from(container.querySelectorAll('.js-filter[data-index]'));
    if (!filterNodes.length) return;

    const preorderNodes = [];
    let availabilityNode = null;

    filterNodes.forEach((node) => {
      const firstInput = node.querySelector('input[name]');
      const paramName = firstInput ? firstInput.getAttribute('name').toLowerCase() : '';
      const labelElement = node.querySelector('.facets__summary span, .mobile-facets__summary span');
      const labelText = labelElement ? labelElement.textContent : '';
      const isPreorderByParam = paramName.includes('custom.pre_order');
      const isAvailabilityByParam = paramName.includes('availability');

      if (isPreorderByParam || FacetFiltersForm.isPreorderLabel(labelText)) {
        preorderNodes.push(node);
      } else if (!availabilityNode && (isAvailabilityByParam || FacetFiltersForm.isAvailabilityLabel(labelText))) {
        availabilityNode = node;
      }
    });

    if (!availabilityNode || !preorderNodes.length) return;

    preorderNodes.forEach((node) => {
      if (node !== availabilityNode) {
        availabilityNode.parentNode.insertBefore(node, availabilityNode);
      }
    });
  }

  static movePreorderBeforeAvailability() {
    FacetFiltersForm.reorderFacetGroup(document.getElementById('FacetsWrapperDesktop'));
    FacetFiltersForm.reorderFacetGroup(document.querySelector('#FacetFiltersFormMobile .mobile-facets__main'));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.movePreorderBeforeAvailability();
    FacetFiltersForm.syncPriceActiveFacets();

    if (window.$body && typeof window.$body.trigger === 'function') {
      window.$body.trigger('filterUpdated');
    }
    document.dispatchEvent(new CustomEvent('rw:facets-updated'));

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const currentActiveFacets = document.querySelector(selector);
      if (!currentActiveFacets) return;
      currentActiveFacets.innerHTML = activeFacetsElement.innerHTML;
      if (currentActiveFacets.classList.contains('rw-collection-active-filters')) {
        currentActiveFacets.toggleAttribute('hidden', activeFacetsElement.hasAttribute('hidden'));
      }
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const desktopCount = document.querySelector('.rw-desktop-facets__count');
    const nextDesktopCount = html.querySelector('.rw-desktop-facets__count');
    if (desktopCount && nextDesktopCount) {
      desktopCount.innerHTML = nextDesktopCount.innerHTML;
      desktopCount.toggleAttribute('hidden', nextDesktopCount.hasAttribute('hidden'));
    }

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static syncPriceActiveFacets() {
    const priceRange = document.querySelector('price-range');
    if (!priceRange) return;

    const inputs = priceRange.querySelectorAll('input');
    if (inputs.length < 2) return;

    const minValue = PriceRange.extractNumber(inputs[0].value);
    const maxValue = PriceRange.extractNumber(inputs[1].value);
    const minText = `${PriceRange.formatForDisplay(minValue ?? 0)} IQD`;
    const maxFallback = PriceRange.extractNumber(inputs[1].getAttribute('max')) ?? 0;
    const maxText = `${PriceRange.formatForDisplay(maxValue ?? maxFallback)} IQD`;

    document.querySelectorAll('.rw-price-active-facet').forEach((facetButton) => {
      const minNode = facetButton.querySelector('.rw-price-active-facet__min');
      const maxNode = facetButton.querySelector('.rw-price-active-facet__max');
      if (minNode) minNode.textContent = minText;
      if (maxNode) maxNode.textContent = maxText;
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      if (!formData.has(key)) return;
      const sanitized = PriceRange.toQueryValue(formData.get(key));
      if (sanitized === '') {
        formData.delete(key);
      } else {
        formData.set(key, sanitized);
      }
    });
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onDeferredSubmit(event) {
    event.preventDefault();

    this.querySelectorAll('price-range').forEach((range) => {
      if (typeof range.enforceLimits === 'function') range.enforceLimits();
      if (typeof range.setMinAndMaxValues === 'function') range.setMinAndMaxValues();
    });

    this.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    this.onSubmitHandler(event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.boundInputHandler = this.onInput.bind(this);
    this.boundChangeHandler = this.onRangeChange.bind(this);
    this.boundBlurHandler = this.onBlur.bind(this);
    this.boundFocusHandler = this.onFocus.bind(this);
    this.inputs = Array.from(this.querySelectorAll('input'));

    this.inputs.forEach((element) => {
      element.setAttribute('inputmode', 'numeric');
      element.setAttribute('autocomplete', 'off');
      element.addEventListener('input', this.boundInputHandler);
      element.addEventListener('change', this.boundChangeHandler);
      element.addEventListener('blur', this.boundBlurHandler);
      element.addEventListener('focus', this.boundFocusHandler);
      this.formatInputValue(element);
    });

    this.setRangeMaximum();
    this.enforceLimits();
    this.setMinAndMaxValues();
  }

  static normalizeDigits(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
      .replace(/[٬،]/g, ',');
  }

  static extractNumber(value) {
    const normalized = PriceRange.normalizeDigits(value).replace(/\s+/g, '');
    const digitsOnly = normalized.replace(/\D/g, '');
    if (!digitsOnly) return null;

    const parsed = Number.parseInt(digitsOnly, 10);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(0, parsed);
  }

  static formatForDisplay(value) {
    if (!Number.isFinite(value)) return '';
    return value.toLocaleString('en-US');
  }

  static toQueryValue(value) {
    const parsed = PriceRange.extractNumber(value);
    return parsed === null ? '' : String(parsed);
  }

  getInputValues() {
    const minInput = this.inputs[0];
    const maxInput = this.inputs[1];
    return {
      minInput,
      maxInput,
      minValue: PriceRange.extractNumber(minInput && minInput.value),
      maxValue: PriceRange.extractNumber(maxInput && maxInput.value)
    };
  }

  setRangeMaximum() {
    const { minInput, maxInput } = this.getInputValues();
    const maxAttr = (maxInput && maxInput.getAttribute('max')) || (minInput && minInput.getAttribute('max')) || '0';
    this.rangeMaxValue = PriceRange.extractNumber(maxAttr) || 0;
  }

  enforceLimits() {
    const { minInput, maxInput, minValue, maxValue } = this.getInputValues();
    let nextMin = minValue;
    let nextMax = maxValue;

    if (nextMin !== null) nextMin = Math.min(Math.max(nextMin, 0), this.rangeMaxValue);
    if (nextMax !== null) nextMax = Math.min(Math.max(nextMax, 0), this.rangeMaxValue);

    if (nextMin !== null && nextMax !== null && nextMin > nextMax) {
      if (document.activeElement === minInput) {
        nextMax = nextMin;
      } else {
        nextMin = nextMax;
      }
    }

    this.writeValue(minInput, nextMin);
    this.writeValue(maxInput, nextMax);
  }

  writeValue(input, numericValue) {
    if (!input) return;
    if (numericValue === null || !Number.isFinite(numericValue)) {
      input.value = '';
      return;
    }
    input.value = String(numericValue);
  }

  formatInputValue(input) {
    const parsed = PriceRange.extractNumber(input.value);
    if (parsed === null) {
      input.value = '';
      return;
    }
    input.value = PriceRange.formatForDisplay(parsed);
  }

  onInput(event) {
    const input = event.currentTarget;
    const parsed = PriceRange.extractNumber(input.value);
    if (parsed === null) {
      input.value = '';
      return;
    }
    input.value = String(parsed);
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.enforceLimits();
    this.setMinAndMaxValues();
    FacetFiltersForm.syncPriceActiveFacets();
  }

  setMinAndMaxValues() {
    const { minInput, maxInput, minValue, maxValue } = this.getInputValues();
    if (!minInput || !maxInput) return;

    if (maxValue !== null) minInput.setAttribute('max', String(maxValue));
    else minInput.setAttribute('max', String(this.rangeMaxValue));

    if (minValue !== null) maxInput.setAttribute('min', String(minValue));
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', String(this.rangeMaxValue));
  }

  adjustToValidValues(input) {
    const parsed = PriceRange.extractNumber(input.value);
    if (parsed === null) {
      input.value = '';
      return;
    }
    const min = PriceRange.extractNumber(input.getAttribute('min')) || 0;
    const max = PriceRange.extractNumber(input.getAttribute('max')) || this.rangeMaxValue;
    const clamped = Math.min(Math.max(parsed, min), max);
    input.value = String(clamped);
  }

  onBlur(event) {
    const input = event.currentTarget;
    this.adjustToValidValues(input);
    this.enforceLimits();
    this.setMinAndMaxValues();
    FacetFiltersForm.syncPriceActiveFacets();
    this.formatInputValue(input);
  }

  onFocus(event) {
    const input = event.currentTarget;
    const parsed = PriceRange.extractNumber(input.value);
    input.value = parsed === null ? '' : String(parsed);
  }
}

customElements.define('price-range', PriceRange);
window.PriceRange = PriceRange;

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

function initializeCategoryReturnHeader() {
  const header = document.querySelector('[data-rw-collection-return]');
  if (!header || !window.matchMedia('(max-width: 989px)').matches) return;

  let returnState = null;
  try {
    returnState = JSON.parse(window.sessionStorage.getItem('rwModernCategoryReturn') || 'null');
  } catch (error) {
    returnState = null;
  }

  if (!returnState || returnState.destinationPath !== window.location.pathname) return;

  let returnUrl = null;
  try {
    returnUrl = new URL(returnState.returnUrl, window.location.origin);
    if (returnUrl.origin !== window.location.origin) returnUrl = null;
  } catch (error) {
    returnUrl = null;
  }
  if (!returnUrl) return;

  header.hidden = false;
  header.closest('.rw-collection-heading')?.classList.add('has-category-return');

  const pathLabels = Array.isArray(returnState.pathLabels)
    ? returnState.pathLabels.filter((label) => typeof label === 'string' && label.trim())
    : [];
  const returnPath = header.querySelector('[data-rw-collection-return-path]');
  if (returnPath && pathLabels.length) {
    const separator = document.documentElement.dir === 'rtl' ? ' ← ' : ' → ';
    returnPath.textContent = pathLabels.join(separator);
  }

  header.querySelector('[data-rw-collection-all-button]')?.addEventListener('click', (event) => {
    const categoriesUrl = event.currentTarget.getAttribute('data-categories-url') || '/collections';
    window.sessionStorage.removeItem('rwModernCategoryReturn');
    window.location.assign(categoriesUrl);
  });

  header.querySelector('[data-rw-collection-return-button]')?.addEventListener('click', (event) => {
    const fallbackUrl = event.currentTarget.getAttribute('data-fallback-url') || '/collections';
    window.sessionStorage.removeItem('rwModernCategoryReturn');
    window.location.assign(returnUrl?.href || fallbackUrl);
  });
}

initializeCategoryReturnHeader();

document.addEventListener('click', (event) => {
  const sortTrigger = event.target.closest('[data-rw-mobile-sort-trigger]');
  if (!sortTrigger) return;

  const facetsContainer = sortTrigger.closest('.facets-container');
  const drawer = facetsContainer?.querySelector('menu-drawer.mobile-facets__wrapper');
  const disclosure = drawer?.querySelector(':scope > .mobile-facets__disclosure');
  const summary = disclosure?.querySelector(':scope > summary');
  if (!drawer || !disclosure || !summary) return;

  if (!disclosure.open) summary.click();

  window.setTimeout(() => {
    const sortPanel = disclosure.querySelector('.mobile-facets__sort');
    const drawerMain = disclosure.querySelector('.mobile-facets__main');
    if (!sortPanel || !drawerMain) return;

    drawerMain.scrollTo({
      top: Math.max(0, sortPanel.closest('.mobile-facets__details').offsetTop - 12),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
    sortPanel.querySelector('select')?.focus({ preventScroll: true });
  }, disclosure.classList.contains('menu-opening') ? 0 : 420);
});
